import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import { Box, Breadcrumbs, Container, Divider, Grid, Link as MuiLink, Typography } from '@mui/material';
import filter from 'lodash.filter';
import reduce from "lodash.reduce";
import dynamic from "next/dynamic";
import React, { useMemo } from "react";
import useSWR from "swr";
import getPageMetaTags from "../../lib/meta/_getMetaTags";
import getSectorsAndMetrics from "../../lib/sectors/_getSectorsAndMetrics";
import getStockTabs from "../../lib/stock/_getStockTabsData.js";
import IndustriesList from "../../src/components/Sectors/IndustriesList.js";
import SectorHeatMap from "../../src/components/Sectors/SectorHeatMap/index.js";
import { dataConstants } from "../../src/constants";
import { formatCount } from "../../src/utils/D3/helper";
import _getLatestPrice from "../api/_getLatestPrice.js";

const SwipeableEdgeDrawer = dynamic(() => import('../../src/components/Sectors/SwipeableDrawer/index.js'))

const calculateMarketData = (data) => {
  const uniqueIndustries = new Set();
  const uniqueSectors = new Set();
  let combinedMarketCap = 0;

  data.forEach(item => {
    if (item.industry) uniqueIndustries.add(item.industry);
    if (item.sector) uniqueSectors.add(item.sector);
    if (item.MKTCAP) combinedMarketCap += item.MKTCAP;
  });

  return {
    uniqueIndustries: uniqueIndustries.size,
    uniqueSectors: uniqueSectors.size,
    combinedMarketCap
  };
};

const convertData = (data) => {
  return reduce(data, (acc, { ticker, r, p }) => {
    acc[ticker] = acc[ticker] || { ticker, price_data: [] };
    acc[ticker] = { ...acc[ticker], price_data: [...acc[ticker].price_data, { price: p }] };
    return acc;
  }, {});
};

export const isExponent = num => {
  const n = num.toString();
  return /[-+]?[0-9]*\.?[0-9]+([eE][-+]?[0-9]+)/g.test(n);
};

export const roundNumb = (num, round = 2) => {
  if (!num || isNaN(parseFloat(num))) return num; // Ensure num is numeric
  if (isExponent(num)) {
    const [base, exponent] = num.toString().split('e');
    return parseFloat(`${parseFloat(base).toFixed(round)}e${exponent}`);
  }
  return parseFloat((Math.round(parseFloat(num) * Math.pow(10, round)) / Math.pow(10, round)).toFixed(round));
};

export const getPercent = (a, b) => {
  return roundNumb((parseFloat(a) - parseFloat(b)) * 100 / parseFloat(b), 2); // Ensure a and b are numeric
};

const getPCHG = (sectorMetrics) => {
  const { data, error, isValidating } = useSWR(
    ["api/_getLatestPrice", sectorMetrics?.map(d => d.ticker)],
    _getLatestPrice,
    {
      refreshInterval: 10 * 60 * 1000,  // 10 minutes refresh,
      revalidateOnFocus: false,
      onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
        if (error.status === 404 || key === 'api/_getLatestPrice' || retryCount >= 10) return;
        setTimeout(() => revalidate({ retryCount }), 10 * 60 * 1000);
      }
    }
  ) || {};

  const { getLatestPrice: latestPrices } = data || {};

  const mergedData = useMemo(() => {
    if (!latestPrices) return sectorMetrics;
    const priceData = convertData(latestPrices);

    return sectorMetrics.map(item => {
      const tickerData = priceData[item.ticker];
      if (tickerData && tickerData.price_data.length >= 2) {
        const [latestPrice, previousPrice] = tickerData.price_data;
        item.PCHG = parseFloat(getPercent(latestPrice.price, previousPrice.price));
        item.price = latestPrice.price;
      }
      return item;
    });
  }, [latestPrices, sectorMetrics]);

  return {
    data: mergedData,
    isLoading: !data && !error,
    isValidating
  };
};

const SectorPage = ({ sectorList, themeMode, sectorHeatmapMeta }) => {
  const { getWideScreenerMetricsV3: groupedSectors } = sectorList || {};
  const { data: sectorsData, error, isValidating } = getPCHG(groupedSectors);
  const filteredSectors = filter(sectorsData, item => {
    return (item.sector !== null && item.sector !== "");
  });

  const { combinedMarketCap, uniqueIndustries, uniqueSectors } = filteredSectors && filteredSectors.length > 0 ? calculateMarketData(filteredSectors) : {};

  const statItems = [
    { label: 'Sectors', value: uniqueSectors },
    { label: 'Industries', value: uniqueIndustries },
    { label: 'Market Cap', value: combinedMarketCap ? `$${formatCount(Math.sign(combinedMarketCap) * combinedMarketCap, true, 1, Math.sign(combinedMarketCap))}` : '—' },
  ];

  return (
    <Container maxWidth="xl" sx={{ pb: 6 }}>
      {/* Breadcrumb */}
      <Box component="nav" sx={{ pt: { xs: 1.5, sm: 2 }, pb: 0.5 }}>
        <Breadcrumbs
          separator={<NavigateNextRoundedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}
          aria-label="breadcrumb"
        >
          <MuiLink
            underline="hover"
            href="/"
            sx={{ color: 'text.secondary', fontSize: '0.8rem', lineHeight: 1, '&:hover': { color: 'primary.main' } }}
          >
            Home
          </MuiLink>
          <Typography sx={{ color: 'text.primary', fontSize: '0.8rem', fontWeight: 500, lineHeight: 1 }}>
            Sectors
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Page Header */}
      <Box sx={{ pt: 1, pb: { xs: 2, sm: 2.5 } }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: '1.5rem', sm: '1.75rem' },
            fontWeight: 700,
            color: 'text.primary',
            letterSpacing: '-0.01em',
            lineHeight: 1.3,
          }}
        >
          Sectors
        </Typography>
        <Typography
          variant="body2"
          sx={{
            mt: 0.5,
            fontSize: '0.9rem',
            color: 'rgb(100, 116, 139)',
            lineHeight: 1.5,
            maxWidth: 560,
          }}
        >
          Explore sector performance and market dynamics at a glance.
        </Typography>
      </Box>

      {/* Market Stats Bar */}
      {/* {filteredSectors && filteredSectors.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: { xs: 2, sm: 4 },
            pb: 2,
            mb: 2.5,
            borderBottom: '1px solid',
            borderColor: 'rgba(0, 0, 0, 0.06)',
          }}
        >
          {statItems.map((stat) => (
            <Box key={stat.label}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: 'rgb(148, 163, 184)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  lineHeight: 1,
                }}
              >
                {stat.label}
              </Typography>
              <Typography
                sx={{
                  mt: 0.25,
                  fontSize: '1.05rem',
                  fontWeight: 700,
                  color: 'text.primary',
                  lineHeight: 1.3,
                }}
              >
                {stat.value ?? '—'}
              </Typography>
            </Box>
          ))}
        </Box>
      )} */}

      {/* Mobile Industry Drawer */}
      <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
        <SwipeableEdgeDrawer
          text="Browse Industries"
          industryName={null}
          popularIndustries={null}
          subTab={true}
        />
      </Box>

      {/* Main Content Grid */}
      <Grid container columnSpacing={2.5} sx={{ alignItems: 'start' }}>
        <IndustriesList
          industryName={null}
          popularIndustries={null}
          subTab={true}
          sx={{ display: { xs: 'none', sm: 'grid', md: 'grid' } }}
        />
        <Grid size={{ xs: 12, sm: 8.5, md: 10 }} sx={{ mt: { xs: 1, sm: 0 } }}>
          <SectorHeatMap
            groupedSectors={filteredSectors}
            heatmapColorSettings={sectorHeatmapMeta ? sectorHeatmapMeta.settings : null}
            isLoading={isValidating}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export async function getStaticProps() {
  const groupedSectors = await getSectorsAndMetrics();
  const sector_tab_data = (await getStockTabs("AAPL", "SECTOR", "COLR", null, null, null)) || [];
  const { getStockTabs: secTab } = sector_tab_data || {}
  let heatmapData = secTab && secTab.length > 0 ? secTab.find(d => d.section_code === "heatmap") : null;
  const metaTags = await getPageMetaTags('/sectors', false) || {};
  const { getMetaTags } = metaTags || {};

  return {
    props: {
      meta_tags: getMetaTags?.tags || null,
      sectorList: groupedSectors || null,
      sectorHeatmapMeta: heatmapData || {}
    },
    revalidate: dataConstants.REVALIDATE_IN.DEFAULT_HOURS,
  };
}

export default SectorPage;
